import { API_ROOT_URL, CASCADE_MAP_SERVICE_URL } from '@/lib/constants/api';
import { apiFetch } from '@/lib/utils/apiFetch';
import type {
  BulkIdsPayload,
  CollaborationMapResponse,
  CompanyChainDetailResponse,
  CompanyChainGraphResponse,
  CompanyChainListResponse,
  CompanyChainRow,
  CompanyLinkPayload,
  CreateSupplyChainPayload,
  CreateSupplyChainResponse,
  CriticalSupplier,
  FacilityProcessOverviewResponse,
  GlobalDiagramResponse,
  InsightsHistoryResponse,
  InvitableCompaniesResponse,
  InviteCollaboratorPayload,
  LinkCompaniesPayload,
  MappedCompaniesListResponse,
  ReplaceCompanyPayload,
  ReuseCompanyChainPayload,
  SupplierDetailsResponse,
  SupplierStatistics,
  UnmappedCompaniesResponse,
  UpdateMappingDepthPayload,
  UpdateNodeFacilityProcessesPayload,
  UpdateSupplyChainMetadataPayload,
  UpdateSupplyChainStatusPayload,
} from './types';

export async function fetchCompanyChainGraph(
  collaborationId: string,
): Promise<CompanyChainGraphResponse> {
  return apiFetch<CompanyChainGraphResponse>(
    `/v1/company-chains/collaborations/${collaborationId}/expand-all`,
    {},
    CASCADE_MAP_SERVICE_URL,
  );
}

export async function fetchCompanyChainDetail(
  collaborationId: string,
): Promise<CompanyChainDetailResponse> {
  return apiFetch<CompanyChainDetailResponse>(
    `/v1/company-chains/collaborations/${collaborationId}`,
    {},
    CASCADE_MAP_SERVICE_URL,
  );
}

export async function fetchCompanyChains(
  qs: string,
): Promise<CompanyChainListResponse> {
  return apiFetch<CompanyChainListResponse>(
    `/v1/company-chains/collaborations?${qs}`,
    {},
    CASCADE_MAP_SERVICE_URL,
  );
}

export async function bulkArchiveCompanyChains(
  body: BulkIdsPayload,
): Promise<unknown> {
  return apiFetch<unknown>(
    '/v1/company-chains/bulk-archive',
    { method: 'POST', body: JSON.stringify(body) },
    CASCADE_MAP_SERVICE_URL,
  );
}

export async function bulkUnarchiveCompanyChains(
  body: BulkIdsPayload,
): Promise<unknown> {
  return apiFetch<unknown>(
    '/v1/company-chains/bulk-unarchive',
    { method: 'POST', body: JSON.stringify(body) },
    CASCADE_MAP_SERVICE_URL,
  );
}

export async function bulkExportCompanyChains(
  body: BulkIdsPayload,
): Promise<unknown> {
  return apiFetch<unknown>(
    '/v1/company-chains/export',
    { method: 'POST', body: JSON.stringify(body) },
    CASCADE_MAP_SERVICE_URL,
  );
}

export async function createCompanySupplyChain(
  body: CreateSupplyChainPayload,
): Promise<CreateSupplyChainResponse> {
  return apiFetch<CreateSupplyChainResponse>(
    '/v1/company-chains',
    { method: 'POST', body: JSON.stringify(body) },
    CASCADE_MAP_SERVICE_URL,
  );
}

export async function updateSupplyChainMetadata(
  collaborationId: string,
  body: UpdateSupplyChainMetadataPayload,
): Promise<unknown> {
  return apiFetch<unknown>(
    `/v1/company-chains/collaborations/${collaborationId}/metadata`,
    { method: 'PATCH', body: JSON.stringify(body) },
    CASCADE_MAP_SERVICE_URL,
  );
}

export async function updateMappingDepth(
  collaborationId: string,
  body: UpdateMappingDepthPayload,
): Promise<unknown> {
  return apiFetch<unknown>(
    `/v1/company-chains/collaborations/${collaborationId}/mapping-depth`,
    { method: 'PATCH', body: JSON.stringify(body) },
    CASCADE_MAP_SERVICE_URL,
  );
}

export async function fetchUnmappedCompaniesForNode(
  nodeId: string,
  qs: string,
): Promise<UnmappedCompaniesResponse> {
  return apiFetch<UnmappedCompaniesResponse>(
    `/v1/company-chains/nodes/${nodeId}/unmapped-company-profiles?${qs}`,
    {},
    CASCADE_MAP_SERVICE_URL,
  );
}

export async function linkCompaniesIntoSupplyChain(
  collaborationId: string,
  body: LinkCompaniesPayload,
): Promise<unknown> {
  return apiFetch<unknown>(
    `/v1/company-chains/collaborations/${collaborationId}`,
    { method: 'PATCH', body: JSON.stringify(body) },
    CASCADE_MAP_SERVICE_URL,
  );
}

export async function updateNodeFacilityProcesses(
  nodeId: string,
  body: UpdateNodeFacilityProcessesPayload,
): Promise<unknown> {
  return apiFetch<unknown>(
    `/v1/company-chains/nodes/${nodeId}`,
    { method: 'PATCH', body: JSON.stringify(body) },
    CASCADE_MAP_SERVICE_URL,
  );
}

export async function deleteSupplyChainNode(nodeId: string): Promise<unknown> {
  return apiFetch<unknown>(
    `/v1/company-chains/nodes/${nodeId}`,
    { method: 'DELETE' },
    CASCADE_MAP_SERVICE_URL,
  );
}

export async function sendInvitationReminder(
  collaborationId: string,
  body: { companyId: string },
): Promise<unknown> {
  return apiFetch<unknown>(
    `/v1/company-chains/collaborations/${collaborationId}/invitation-reminders`,
    { method: 'PATCH', body: JSON.stringify(body) },
    CASCADE_MAP_SERVICE_URL,
  );
}

export async function updateSupplyChainStatus(
  collaborationId: string,
  body: UpdateSupplyChainStatusPayload,
): Promise<unknown> {
  return apiFetch<unknown>(
    `/v1/company-chains/collaborations/${collaborationId}/status`,
    { method: 'PATCH', body: JSON.stringify(body) },
    CASCADE_MAP_SERVICE_URL,
  );
}

// Lives on the main API gateway (`/api/companies/link`), NOT on the
// companies-service microservice.
export async function connectCompanyAsSupplier(
  body: CompanyLinkPayload,
): Promise<unknown> {
  return apiFetch<unknown>(
    '/api/companies/link',
    { method: 'POST', body: JSON.stringify(body) },
    API_ROOT_URL,
  );
}

export async function inviteSupplyChainCollaborators(
  body: InviteCollaboratorPayload,
): Promise<unknown> {
  return apiFetch<unknown>(
    '/v1/supply-chain-collaborators',
    { method: 'POST', body: JSON.stringify(body) },
    CASCADE_MAP_SERVICE_URL,
  );
}

export async function duplicateCompanyChain(
  collaborationId: string,
): Promise<{ data: CompanyChainRow }> {
  return apiFetch<{ data: CompanyChainRow }>(
    `/v1/company-chains/${collaborationId}/duplicate`,
    { method: 'POST' },
    CASCADE_MAP_SERVICE_URL,
  );
}

export async function reuseCompanyChain(
  body: ReuseCompanyChainPayload,
): Promise<unknown> {
  return apiFetch<unknown>(
    '/v1/company-chains/reuse',
    { method: 'PATCH', body: JSON.stringify(body) },
    CASCADE_MAP_SERVICE_URL,
  );
}

export async function fetchInvitableCompanies(
  collaborationId: string,
  qs: string,
): Promise<InvitableCompaniesResponse> {
  return apiFetch<InvitableCompaniesResponse>(
    `/v1/company-chains/${collaborationId}/companies?${qs}`,
    {},
    CASCADE_MAP_SERVICE_URL,
  );
}

// ── Insights ─────────────────────────────────────────────────────────────────

export async function fetchSupplierStatistics(): Promise<SupplierStatistics> {
  return apiFetch<SupplierStatistics>(
    '/v1/supplier-statistics/',
    {},
    CASCADE_MAP_SERVICE_URL,
  );
}

export async function fetchInsightsHistory(
  qs: string,
): Promise<InsightsHistoryResponse> {
  return apiFetch<InsightsHistoryResponse>(
    `/v1/company-chains/history?${qs}`,
    {},
    CASCADE_MAP_SERVICE_URL,
  );
}

export async function fetchMappedCompanies(
  qs: string,
): Promise<MappedCompaniesListResponse> {
  return apiFetch<MappedCompaniesListResponse>(
    `/v1/company-profiles/company-chains/mapped-companies?${qs}`,
    {},
    CASCADE_MAP_SERVICE_URL,
  );
}

export async function fetchSupplierDetails(
  companyId: string,
): Promise<SupplierDetailsResponse> {
  return apiFetch<SupplierDetailsResponse>(
    `/v1/company-chains/insights/suppliers/${companyId}`,
    {},
    CASCADE_MAP_SERVICE_URL,
  );
}

export async function fetchCollaborationMap(
  collaborationId: string,
): Promise<CollaborationMapResponse> {
  return apiFetch<CollaborationMapResponse>(
    `/v1/company-chains/collaborations/${collaborationId}/map`,
    {},
    CASCADE_MAP_SERVICE_URL,
  );
}

export async function fetchCollaboratorCompanies(
  qs: string,
): Promise<MappedCompaniesListResponse> {
  return apiFetch<MappedCompaniesListResponse>(
    `/v1/company-chains/collaborators?${qs}`,
    {},
    CASCADE_MAP_SERVICE_URL,
  );
}

export async function fetchCriticalSuppliers(): Promise<CriticalSupplier[]> {
  return apiFetch<CriticalSupplier[]>(
    '/v1/supplier-statistics/critical-suppliers',
    {},
    CASCADE_MAP_SERVICE_URL,
  );
}

export async function fetchFacilityProcessOverview(): Promise<FacilityProcessOverviewResponse> {
  return apiFetch<FacilityProcessOverviewResponse>(
    '/v1/supplier-statistics/facility-process-overview',
    {},
    CASCADE_MAP_SERVICE_URL,
  );
}

export async function fetchGlobalDiagram(): Promise<GlobalDiagramResponse> {
  return apiFetch<GlobalDiagramResponse>(
    '/v1/company-chains/insights/global-diagram/',
    {},
    CASCADE_MAP_SERVICE_URL,
  );
}

export async function replaceSupplyChainNode(
  nodeId: string,
  body: ReplaceCompanyPayload,
): Promise<void> {
  return apiFetch<void>(
    `/v1/company-chains/nodes/${nodeId}/replace`,
    { method: 'PATCH', body: JSON.stringify(body) },
    CASCADE_MAP_SERVICE_URL,
  );
}

export async function acceptCollaborationInvitation(
  collaborationId: string,
  body: { collaborationName?: string },
): Promise<void> {
  return apiFetch<void>(
    `/v1/company-chains/collaborations/${collaborationId}/accept`,
    { method: 'PATCH', body: JSON.stringify(body) },
    CASCADE_MAP_SERVICE_URL,
  );
}
